<script>
import {getFrsList, getNewsletterDatas} from "@/utils/frs";
import Substitute from "@/components/Substitute.vue";
import Chart from "chart.js";
import dayjs from "dayjs";
import * as htmlToImage from 'html-to-image';

export default {
  components: {Substitute},
  data() {
    return {
      caChart: null,
      rfaChart: null,
      pdmChart: null,
      penChart: null,
      loaded: false,
      selectedFrsGuid: "",
      frsList: [],
      datas: {},
      base64Newsletter: "",
      scraped: false
    }
  },
  mounted() {
    let userInfos = this.$store.state.self;
    if (userInfos.isSupplier && !userInfos.isAdmin) {
      this.selectedFrsGuid = userInfos.guid;
    } else if (userInfos.isAdmin) {
      const urlParams = new URLSearchParams(window.location.search);
      const frsGuid = urlParams.get('frsGuid');
      if (frsGuid) {
        this.scraped = true;
        this.selectedFrsGuid = frsGuid;
      }
    }

    getFrsList().then(res => {
      this.frsList = res
    });
  },
  watch: {
    selectedFrsGuid() {
      if (!this.selectedFrsGuid) return;

      this.loaded = false;
      this.base64Newsletter = "";
      getNewsletterDatas(this.selectedFrsGuid).then(res => {
        this.datas = res;
        this.loaded = true;
        this.$nextTick(() => {
          this.renderCAChart();
          // this.renderRFAChart();
          this.renderPdmChart();
          this.renderPenChart();

          // On génère le rendu en base 64 pour extraction via scrapper
          window.setTimeout(() => {
            this.generateBase64Newsletter();
          }, 500)
        })
      })
    }
  },
  computed: {
    // Récupère les données du founisseur à partir du Guid du fournisseur sélectionné (selectedFrsGuid)
    selectedFrs() {
      if (this.selectedFrsGuid) return this.frsList.find(el => el.Guid == this.selectedFrsGuid);
      return null;
    },
  },
  methods: {
    dayjs,
    generateBase64Newsletter: async function () {
      htmlToImage.toBlob(document.getElementById('newsletter-inner-content'))
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            // Le résultat est une string au format "data:image/png;base64,..."
            // On extrait uniquement la partie base64 après la virgule
            this.base64Newsletter = reader.result.split(',')[1];
            console.log(this.base64Newsletter);
          };
          reader.readAsDataURL(blob);
        });
    },
    scaleDataAxesToUnifyZeroes(datasets, options) {
      let axes = options.scales.yAxes
      let axesInfos = {}
      datasets.forEach(function (dataset) {
        let infos = {
          min_value: Math.min(...dataset.data, 0),
          max_value: Math.max(...dataset.data),
        }

        infos.range = infos.max_value - infos.min_value;
        if (infos.range !== 0) {
          infos.min_ratio = infos.min_value / infos.range;
          infos.max_ratio = infos.max_value / infos.range;
        } else {
          infos.min_ratio = 1;
          infos.max_ratio = 1;
        }

        axesInfos[dataset.yAxisID] = infos;
      })

      let cap_min_ratio = Math.min(...Object.values(axesInfos).map(infos => infos.min_ratio))
      let cap_max_ratio = Math.max(...Object.values(axesInfos).map(infos => infos.max_ratio))

      axes.forEach(axis => {
        let infos = axesInfos[axis.id]
        axis.ticks = axis.ticks || {}
        axis.ticks.min = Math.min(cap_min_ratio * infos.range, 0);
        axis.ticks.max = cap_max_ratio * infos.range;
      })
    },
    hideFirstAndLastTicks(scaleInstance) {
      // On supprime le premier tick (tout en haut du graphique)
      scaleInstance.ticks[0] = null;
      // 2e tableau utilisé en interne par ChartJS
      scaleInstance.ticksAsNumbers[0] = null;

      // Si le premier tick (en bas) est différent du 0, on le supprime également
      if (scaleInstance.ticksAsNumbers[scaleInstance.ticksAsNumbers.length - 1] !== 0) {
        scaleInstance.ticks[scaleInstance.ticks.length - 1] = null;
        scaleInstance.ticksAsNumbers[scaleInstance.ticksAsNumbers.length - 1] = null;
      }
    },
    renderCAChart() {
      const ctx = document.getElementById("caChart").getContext("2d");

      // Extraction des années, des valeurs de CA et des ProgCA depuis les données (this.datas)
      const years = this.datas.map((data) => data.Year);
      const caValues = this.datas.map((data) => data.CA);
      const progCaValues = this.datas.map((data) => data.ProgCA); // Nouvelle donnée pour ProgCA

      if (this.caChart) this.caChart.destroy();

      let datasets = [
        {
          type: "line",
          label: "Chiffre d'Affaires annuel (CA)",
          data: caValues,
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          type: "bar",
          label: "Progression CA",
          data: progCaValues,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
          yAxisID: 'y1',
          barPercentage: 0.5,  // Ajout de cette ligne pour réduire la largeur des barres (0.5 = 50% de la largeur par défaut)
        },
      ];

      let options = {
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.yLabel;

              // Si c'est le dataset ProgCA (le deuxième dataset, index 1)
              if (tooltipItem.datasetIndex === 1) {
                return `${label}: ${(value * 100).toFixed(2)}%`;
              }
              // Pour le CA, formatage en euros
              return `${label}: ${new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR'
              }).format(value)}`;
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Années",
            },
          },
          yAxes: [
            {
              id: "y",
              type: "linear",
              position: "left",
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                    maximumFractionDigits: 0
                  }).format(value);
                },
              },
              afterTickToLabelConversion: this.hideFirstAndLastTicks,
            },
            {
              id: "y1",
              type: "linear",
              position: "right",
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return `${(value * 100).toFixed(2)}%`;
                }
              },
              afterTickToLabelConversion: this.hideFirstAndLastTicks,
              gridLines: {
                drawOnChartArea: false,   // Ne pas dessiner les lignes de grille sur la zone du graphique
              }
            }
          ],
        },
        layout: {
          padding: {
            top: 20,
            bottom: 20
          }
        }
      };

      this.scaleDataAxesToUnifyZeroes(datasets, options);

      this.caChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: years,
          datasets,
        },
        options,
      });
    },
    renderRFAChart() {
      const ctx = document.getElementById("rfaChart").getContext("2d");

      const years = this.datas.map((data) => data.Year);
      const rfaValues = this.datas.map((data) => data.RFA);
      const progRfaValues = this.datas.map((data) => data.ProgRFA);

      if (this.rfaChart) this.rfaChart.destroy();

      let datasets = [
        {
          type: "line",
          label: "RFA Annuelle",
          data: rfaValues,
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          type: "bar",
          label: "Progression RFA",
          data: progRfaValues,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
          yAxisID: 'y1',
          barPercentage: 0.5,  // Ajout de cette ligne pour réduire la largeur des barres (0.5 = 50% de la largeur par défaut)
        },
      ];

      let options = {
        responsive: true,
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.yLabel;

              // Si c'est le dataset ProgRFA (le deuxième dataset, index 1)
              if (tooltipItem.datasetIndex === 1) {
                return `${label}: ${(value * 100).toFixed(2)}%`;
              }
              // Pour la RFA, formatage en euros
              return `${label}: ${new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR'
              }).format(value)}`;
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Années",
            },
          },
          yAxes: [
            {
              id: "y",
              type: "linear",
              position: "left",
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                    maximumFractionDigits: 0
                  }).format(value);
                },
              },
              afterTickToLabelConversion: this.hideFirstAndLastTicks,
            },
            {
              id: "y1",
              type: "linear",
              position: "right",
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return value * 100 + ' %';
                }
              },
              afterTickToLabelConversion: this.hideFirstAndLastTicks,
              gridLines: {
                drawOnChartArea: false,   // Ne pas dessiner les lignes de grille sur la zone du graphique
              }
            }
          ],
        },
        layout: {
          padding: {
            top: 20,
            bottom: 20
          }
        }
      };

      this.scaleDataAxesToUnifyZeroes(datasets, options);

      this.rfaChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: years,
          datasets,
        },
        options,
      });
    },
    renderPenChart() {
      const ctx = document.getElementById("penChart").getContext("2d");

      // Extraction des années, des valeurs de CA et des ProgCA depuis les données (this.datas)
      const years = this.datas.map((data) => data.Year);
      const nbValues = this.datas.map((data) => data.NbSocFacture);
      const txValues = this.datas.map((data) => data.TxPenetrationMarche); // Nouvelle donnée pour ProgCA

      if (this.penChart) this.penChart.destroy();

      let datasets = [
        {
          type: "line",
          label: "Nombre de sociétés facturées",
          data: nbValues,
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          type: "bar",
          label: "Taux de pénétration concurrents",
          data: txValues,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
          yAxisID: 'y1',
          barPercentage: 0.5,  // Ajout de cette ligne pour réduire la largeur des barres (0.5 = 50% de la largeur par défaut)
        },
      ];

      let options = {
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.yLabel;

              // Si c'est le dataset ProgCA (le deuxième dataset, index 1)
              if (tooltipItem.datasetIndex === 1) {
                return `${label}: ${(value * 100).toFixed(2)}%`;
              }
              // Pour le CA, formatage en euros
              return `${label}: ${new Intl.NumberFormat('fr-FR', {
                maximumFractionDigits: 0
              }).format(value)}`;
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Années",
            },
          },
          yAxes: [
            {
              id: "y",
              type: "linear",
              position: "left",
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return new Intl.NumberFormat('fr-FR', {
                    maximumFractionDigits: 0
                  }).format(value);
                },
              },
              afterTickToLabelConversion: this.hideFirstAndLastTicks,
            },
            {
              id: "y1",
              type: "linear",
              position: "right",
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return `${(value * 100).toFixed(2)}%`;
                }
              },
              afterTickToLabelConversion: this.hideFirstAndLastTicks,
              gridLines: {
                drawOnChartArea: false,   // Ne pas dessiner les lignes de grille sur la zone du graphique
              }
            }
          ],
        },
        layout: {
          padding: {
            top: 20,
            bottom: 20
          }
        }
      };

      // datasets[1].data.push(0);
      // datasets[1].data.push(0.1);

      this.scaleDataAxesToUnifyZeroes(datasets, options);

      // datasets[1].data.pop();
      // datasets[1].data.pop();

      this.penChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: years,
          datasets,
        },
        options,
      });
    },
    renderPdmChart() {
      const ctx = document.getElementById("pdmChart").getContext("2d");

      const years = this.datas.map((data) => data.Year);
      const pdmMarcheValues = this.datas.map((data) => data.PdmMarche);
      const pdmSousFamilleValues = this.datas.map((data) => data.PdmSousFamille);

      if (this.pdmChart) this.pdmChart.destroy();

      let datasets = [
        {
          type: "line",
          label: "Mon CA vs concurrents",
          data: pdmMarcheValues,
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          type: "line",
          label: "Mon CA vs sous-famille",
          data: pdmSousFamilleValues,
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y',
        }
      ];

      let options = {
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.yLabel;
              return `${label}: ${(value * 100).toFixed(2)}%`;
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Années",
            },
          },
          yAxes: [
            {
              id: "y",
              type: "linear",
              position: "left",
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return `${(value * 100).toFixed(2)}%`;
                },
              },
              afterTickToLabelConversion: this.hideFirstAndLastTicks,
            }
          ],
        },
        layout: {
          padding: {
            top: 20,
            bottom: 20
          }
        }
      };

      this.pdmChart = new Chart(ctx, {
        type: "line",
        data: {
          labels: years,
          datasets,
        },
        options,
      });
    }
  }
}
</script>

<template>
  <div>
    <div v-if="$store.state.self.isAdmin">
      <label>Fournisseur : </label>
      <Substitute
        class="substitute"
        :frsList="frsList"
        :selectedFrsGuid="selectedFrsGuid"
        @update="(guid) => selectedFrsGuid = guid"
      />
    </div>
    <div id="newsletter-base64-content" class="d-none" v-if="base64Newsletter">{{base64Newsletter}}</div>

    <div id="newsletter-content" class="newsletter-content" v-if="loaded">
      <div id="newsletter-inner-content">
        <section>
          <img
            class="header-img"
            src="@/assets/newsletter-header.png"
            alt="CMEM header image"
          />
        </section>
        <section>
          <p v-if="!scraped">
            Bonjour
            <span class="color-primary"
            >{{ $store.state.self.firstname }}
              {{ $store.state.self.name }}</span
            >
          </p>
          <p>
            Vous trouverez ci-dessous vos éléments de progression actualisés en date du
            <span class="color-primary">{{ dayjs().format("DD/MM/YYYY") }}</span>
          </p>
        </section>
        <section class="block-container">
          <div class="block-title">
            <h2>Votre Chiffre d'Affaires</h2>
          </div>
          <div class="block-section span-2">
            <div class="canvas-container" style="max-width: 820px;margin: auto;">
              <canvas id="caChart"></canvas>
            </div>
          </div>
        </section>
        <section class="block-container">
          <div class="block-title">
            <h2>Vos statistiques</h2>
          </div>
          <div class="block-section">
            <div class="canvas-container">
              <canvas id="pdmChart" height="240"></canvas>
            </div>
          </div>
          <div class="block-section">
            <div class="canvas-container">
              <canvas id="penChart" height="240"></canvas>
            </div>
          </div>
        </section>
        <section class="block-container">
          <div class="block-title">
            <h2>Votre positionnement</h2>
          </div>
          <div v-if="this.datas && this.datas.length > 0" class="block-section score-section">
            <h2>Rappel Scoring</h2>
            <div class="score-graph">
              <div class="score-text" :style="{top: ((1 - (this.datas[0].ScoreFrs / 20)) * 100) + '%'}">
                {{
                  'Votre score' + ((this.datas[0].ScoreFrs === Math.floor(this.datas[0].ScoreMoySousFamille)) ? ' et score moyen sous-famille' : '')
                }}
                : <strong>{{ this.datas[0].ScoreFrs }}</strong>
              </div>
              <div v-if="this.datas[0].ScoreFrs !== Math.floor(this.datas[0].ScoreMoySousFamille)" class="score-text"
                   :style="{top: ((1 - (Math.floor(this.datas[0].ScoreMoySousFamille) / 20)) * 100) + '%'}">
                Score moyen sous-famille : <strong>{{ Math.floor(this.datas[0].ScoreMoySousFamille) }}</strong>
              </div>
            </div>
            <div>Vous êtes classé <strong>{{
                this.datas[0].TopFrsScoreSousFamille
              }}{{ this.datas[0].TopFrsScoreSousFamille === 1 ? "er" : "ème" }}</strong> sur
              <strong>{{ this.datas[0].TotFrsSousFamille }}</strong> dans votre
              sous-famille
            </div>
          </div>
          <div v-if="this.datas && this.datas.length > 0" class="block-section classification-section">
            <h2>Classification RSE</h2>
            <div class="classification-graph">
              <div
                :style="{height: Math.floor(this.datas[0].NbClassificationA / this.datas[0].TotFrsSousFamille * 100) + '%'}"
                :data-label="Math.floor(this.datas[0].NbClassificationA / this.datas[0].TotFrsSousFamille * 100) + '%'">
                A
                <div class="my-class" v-if="this.datas[0].ClassificationFrs === 'A'">Votre classification</div>
              </div>
              <div
                :style="{height: Math.floor(this.datas[0].NbClassificationB / this.datas[0].TotFrsSousFamille * 100) + '%'}"
                :data-label="Math.floor(this.datas[0].NbClassificationB / this.datas[0].TotFrsSousFamille * 100) + '%'">
                B
                <div class="my-class" v-if="this.datas[0].ClassificationFrs === 'B'">Votre classification</div>
              </div>
              <div
                :style="{height: Math.floor(this.datas[0].NbClassificationC / this.datas[0].TotFrsSousFamille * 100) + '%'}"
                :data-label="Math.floor(this.datas[0].NbClassificationC / this.datas[0].TotFrsSousFamille * 100) + '%'">
                C
                <div class="my-class" v-if="this.datas[0].ClassificationFrs === 'C'">Votre classification</div>
              </div>
              <div
                :style="{height: Math.floor(this.datas[0].NbClassificationD / this.datas[0].TotFrsSousFamille * 100) + '%'}"
                :data-label="Math.floor(this.datas[0].NbClassificationD / this.datas[0].TotFrsSousFamille * 100) + '%'">
                D
                <div class="my-class" v-if="this.datas[0].ClassificationFrs === 'D'">Votre classification</div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* region layouts */

.newsletter-content {
  max-width: 1024px;
  background-color: #e5e7ed;
  --color: #8bc34a;
  margin: 24px auto 0;
}

.newsletter-content h2 {
  color: var(--color);
  text-transform: uppercase;
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0;
}

.newsletter-content section:not(:first-child) {
  padding: 16px;
}

.newsletter-content section:nth-child(2n) {
  background-color: #e5e7ed;
}

.newsletter-content section:nth-child(2n + 1) {
  background-color: #fff;
}

.header-img {
  max-width: 100%;
}

.block-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 12px;
  row-gap: 12px;
}

.block-container .block-title {
  grid-column: span 2;
  margin-bottom: 12px;
}

.block-container .block-section {
  width: 100%;
  margin-bottom: 12px;
}

.block-container .block-section.span-2 {
  grid-column: span 2;
}

.canvas-container {
  min-height: 240px;
  max-width: 80%;
  margin: auto;
}

@media (max-width: 800px) {
  .block-container {
    grid-template-columns: 1fr;
    justify-items: center;
  }

  .block-container .block-title {
    grid-column: span 1;
  }
}

.color-primary {
  color: var(--color);
}

.green {
  color: var(--color);
}

.red {
  color: #c66;
}

/* endregion */

/* region graphs */

.score-section .score-graph {
  height: 240px;
  width: 32px;
  background: linear-gradient(to bottom, #A7C13C, #F2975C);
  position: relative;
  margin-top: 12px;
}

.score-section .score-graph .score-text {
  position: absolute;
  left: 0;
  width: max-content;
  padding-left: 36px;
  transform: translateY(-50%);
}

.score-section .score-graph .score-text:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  height: 1px;
  width: 32px;
  background: black;
}

.classification-section .classification-graph {
  height: 240px;
  width: 32px;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-left: 2em;
  margin-top: 12px;
}

.classification-section .classification-graph > div {
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  overflow: visible;
  min-height: 1em;
  border: 1px solid black;
  line-height: 1;
}

.classification-section .classification-graph > div::before {
  content: attr(data-label);
  position: absolute;
  top: 0;
  right: calc(100% + 4px);
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  font-size: 10px;
}

.classification-section .classification-graph > div > .my-class {
  position: absolute;
  top: 0;
  left: calc(100% + 16px);
  width: max-content;
  height: 100%;
  display: grid;
  place-items: center;
}

.classification-section .classification-graph > div > .my-class::before {
  content: '';
  position: absolute;
  top: 50%;
  left: -12px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid black;
}

.classification-section .classification-graph > div:not(:first-child) {
  border-top: none;
}

.classification-section .classification-graph > div:nth-child(1) {
  background: #A7C13C;
}

.classification-section .classification-graph > div:nth-child(2) {
  background: rgb(190, 177, 70);
}

.classification-section .classification-graph > div:nth-child(3) {
  background: rgb(215, 163, 81);
}

.classification-section .classification-graph > div:nth-child(4) {
  background: #F2975C;
}

/* endregion */
</style>



// WEBPACK FOOTER //
// src/pages/Fournisseur/Newsletter.vue